import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, User, CreditCard, Mail, Lock, Store, FileText, BookOpen, Bot, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import { useToast } from '../components/ui/ToastManager';
import { storeUtils } from '../data/storeData';
import { MEMBER_TERMS_TEXT, generateTermsHTML } from '../data/textsData';
import { getHomePageText, homePageFormatters } from '../data/homePageTexts';
import { 
  homePageStyles,
  styleUtils,
  buttonStyles 
} from '../styles/components/HomePage.styles';
import { isCreditCardPayment, isBankTransferPayment, isWebBankTransferPayment } from '../utils/paymentMethodUtils';

// ルート定数をインポート
import { ROUTES, NAVIGATION_OPTIONS } from '../config/routes';

import StoreHistoryPage from './StoreHistoryPage';
import PdfViewer from '../components/PdfViewer';
import BankTransferInfoModal from '../components/ui/BankTransferInfoModal';

// 口座振替系（BANK_TRANSFER / WEB_BANK_TRANSFER）はマイページ上で同一フローとして扱う
const isBankTransferLikePayment = (paymentMethod) =>
  isBankTransferPayment(paymentMethod) || isWebBankTransferPayment(paymentMethod);

/**
 * ホーム画面コンポーネント（Firebase連携対応版）
 * 認証・登録完了後のメイン画面（認証が必要）
 * 
 * 支払いステータスに応じて機能を制限:
 * - status: "PENDING" → 決済情報登録待ち（機能制限）
 * - status: "SETUP_PENDING" → 鍵システム設定待ち（機能制限）
 * - status: "ACTIVE" → 正常利用中（通常利用可能）
 */
const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = location.state || {};

  // 店舗履歴ページの表示状態を追加
  const [showStoreHistory, setShowStoreHistory] = useState(false);
  
  // マニュアルビューワーの状態を追加
  const [showManualViewer, setShowManualViewer] = useState(false);
  const [selectedManualType, setSelectedManualType] = useState(null);
  const [selectedManualTitle, setSelectedManualTitle] = useState('');

  // 口座振替情報表示モーダルの状態を追加
  const [showBankTransferInfo, setShowBankTransferInfo] = useState(false);

  // アコーディオンの開閉状態
  const [accountSectionOpen, setAccountSectionOpen] = useState(false);
  const [usageHistorySectionOpen, setUsageHistorySectionOpen] = useState(false);
  const [manualSectionOpen, setManualSectionOpen] = useState(false);
  
  // 新しいAuthContextからユーザー情報を取得
  const {
    currentUser,
    memberData,
    memberLoading, 
    memberError,
    loading,
    logout, 
    isAuthenticated,
    refetchMemberData,
    getCsrfTokenFromCookie
  } = useAuth();

  // RegistrationFlowContextから登録完了処理を取得
  const { completeRegistration } = useRegistrationFlow();
  
  const { showSuccess, showError, showInfo } = useToast();

  /**
   * アコーディオンヘッダーのホバーハンドラー
   */
  const handleAccordionHeaderHover = (e, isEnter) => {
    e.currentTarget.style.backgroundColor = isEnter ? '#f8f9fa' : '#ffffff';
  };

  /**
   * アコーディオンボタンのホバーハンドラー
   */
  const handleAccordionButtonHover = (e, isEnter) => {
    if (isFunctionAccessible()) {
      e.currentTarget.style.backgroundColor = isEnter ? '#f8f9fa' : '#ffffff';
    }
  };

  /**
   * 3DS認証完了後の登録フロー完了処理
   */
  useEffect(() => {
    const hash = window.location.hash;
    
    if (hash === '#registration-complete') {
      
      // ✅ 登録フロー完了処理(Cookieもクリアされる)
      completeRegistration();
      
      showSuccess('会員登録が完了しました！', 5000);
      
      // ✅ ハッシュをクリア
      window.history.replaceState(null, '', '/home');
    }
  }, [completeRegistration, showSuccess]);

  /**
   * 認証状態の確認
   */
  useEffect(() => {
    // 認証されていない場合はログイン画面へリダイレクト
    if (!loading && !isAuthenticated()) {
      navigate(ROUTES.LOGIN, NAVIGATION_OPTIONS.REPLACE);
    }
  }, [loading, isAuthenticated, navigate]);

  /**
   * コンポーネントマウント時に会員データを自動取得
   */
  useEffect(() => {
    const initializeMemberData = async () => {
      // 認証済みで、メンバーデータがまだない場合
      if (isAuthenticated() && !memberData && !memberLoading && !memberError) {
        try {
          // AuthContextのrefetchMemberDataを使用
          await refetchMemberData();
        } catch (error) {
          showError('会員情報の取得に失敗しました。再度ログインしてください。');
        }
      }
    };

  // 認証状態が確定してから実行
  if (!loading) {
    initializeMemberData();
  }
}, [isAuthenticated, memberData, memberLoading, memberError, loading, refetchMemberData, showInfo, showError]);

// 既存の認証チェックのuseEffectも確認
useEffect(() => {
  // 認証されていない場合はログイン画面へリダイレクト
  if (!loading && !isAuthenticated()) {
    navigate(ROUTES.LOGIN, NAVIGATION_OPTIONS.REPLACE);
  }
}, [loading, isAuthenticated, navigate]);

  /**
   * 会員データの再取得（エラー時のリトライ用）
   */
  const handleRefreshMemberData = async () => {
    if (refetchMemberData) {
      try {
        await refetchMemberData();
      } catch (error) {
        showError(getHomePageText('page.memberUpdateFailed'));
      }
    }
  };

  /**
   * ログアウト処理
   */
  const handleLogout = async () => {
    try {
      await logout();
      navigate(ROUTES.LOGIN, NAVIGATION_OPTIONS.REPLACE);
    } catch (error) {
      console.error('Logout error:', error);
      showError(getHomePageText('page.logoutFailed'));
    }
  };

  /**
   * 会員情報編集ページに遷移
   */
  const handleOpenMemberEdit = () => {
    navigate(ROUTES.MEMBER_EDIT);
  };

  /**
   * クレジットカード変更ページに遷移
   */
  const handleOpenCreditCardEdit = () => {
    navigate(ROUTES.CREDIT_CARD_EDIT);
  };

  /**
   * メールアドレス変更ページに遷移
   */
  const handleOpenEmailChange = () => {
    navigate(ROUTES.EMAIL_CHANGE);
  };

  /**
   * パスワード変更ページに遷移
   */
  const handleOpenPasswordChange = () => {
    navigate(ROUTES.PASSWORD_CHANGE);
  };

  /**
   * 店舗履歴ページを開く
   */
  const handleOpenStoreHistory = () => {
    setShowStoreHistory(true);
  };

  /**
   * 店舗履歴ページを閉じる（ホームに戻る）
   */
  const handleBackFromStoreHistory = () => {
    setShowStoreHistory(false);
  };

  /**
   * 支払い情報設定ページへの遷移
   */
  const handleOpenPaymentSettings = () => {
    const paymentMethod = memberData?.paymentMethod || memberData?.payment_method;
    // クレジットカードの場合
    if (isCreditCardPayment(paymentMethod)) {
      navigate(ROUTES.CREDIT_CARD_INPUT, {
        state: { fromHome: true }
      });
      return;
    }
    // 口座振替の場合（WEB口振含む）
    if (isBankTransferLikePayment(paymentMethod)) {
      setShowBankTransferInfo(true);
      return;
    }
    // 支払い方法未設定の場合（デフォルト）
    navigate(ROUTES.PAYMENT_METHOD_SELECT, {
      state: { fromHome: true }
    });
  };

  /**
   * 口座振替情報モーダルを閉じる
   */
  const handleCloseBankTransferInfo = () => {
    setShowBankTransferInfo(false);
  };

  /**
   * 店舗名を取得する関数（テキストデータとstoreUtilsを統合）
   */
  const getStoreName = (storeCode) => {
    // storeUtilsが存在しない場合は早期にフォールバック
    if (!storeUtils?.getStoreName) {
      return homePageFormatters.formatStoreName(storeCode);
    }

    const storeName = storeUtils.getStoreName(storeCode);
    
    // storeName が取得できず、または storeCode と同じ場合はフォールバック
    if (!storeName || storeName === storeCode) {
      return homePageFormatters.formatStoreName(storeCode);
    }

    return storeName;
  };

  /**
   * 別タブで会員規約を開く処理
   */
  const openTermsInNewTab = () => {
    const termsWindow = window.open('', '_blank');
    termsWindow.document.write(generateTermsHTML(MEMBER_TERMS_TEXT));
    termsWindow.document.close();
  };

  /**
   * マニュアルを開く処理
   */
  const handleOpenManual = async (manualId) => {
    try {

      // CSRFトークンを取得
      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        showError('セキュリティトークンの取得に失敗しました。ページを再読み込みして再度お試しください。');
        return;
      }

      // マニュアル名のマッピング
      const manualTitles = {
        'store_layout': '店舗レイアウト',
        'reservation': '予約マニュアル', 
        'robot': 'ロボットマニュアル',
        'smart_lock': 'スマートロック設定マニュアル'
      };

      // その他のマニュアルの場合はモーダルで表示
      setSelectedManualType(manualId);
      setSelectedManualTitle(manualTitles[manualId] || 'マニュアル');
      setShowManualViewer(true);
      
    } catch (error) {
      console.error('[HOME PAGE] Manual open error:', error);
      // CSRFトークン取得失敗の特別な処理
      if (error.message?.includes('CSRF') || error.message?.includes('セキュリティ')) {
        showError('セキュリティトークンの取得に失敗しました。ページを再読み込みして再度お試しください。');
      } else {
        showError('マニュアルを開く際にエラーが発生しました');
      }
    }
  };

  /**
   * マニュアルビューワーを閉じる処理
   */
  const handleCloseManualViewer = () => {
    setShowManualViewer(false);
    setSelectedManualType(null);
    setSelectedManualTitle('');
  };

  /**
   * ユーザーが利用可能な機能かどうかを判定（ACTIVEステータスのみアクセス可能）
   */
  const isFunctionAccessible = () => {
    return memberData?.status === 'ACTIVE';
  };

  /**
   * 会員ステータスの表示テキストを取得
   */
  const getStatusDisplayText = (status) => {
    switch (status) {
      case 'ACTIVE':
        return '利用可能';
      case 'PENDING':
        return '決済情報登録待ち';
      case 'PAYMENT_FAILED':
        return '決済情報登録待ち';
      case 'SETUP_PENDING':
        return 'スマートロック設定待ち';
      default:
        return '設定中';
    }
  };

  /**
   * 会員ステータスの表示色を取得
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return '#28a745'; // 緑色
      case 'PENDING':
        return '#ffc107'; // 黄色
      case 'PAYMENT_FAILED':
        return '#ffc107'; // 黄色
      case 'SETUP_PENDING':
        return '#17a2b8'; // 青色
      default:
        return '#6c757d'; // グレー色
    }
  };

  /**
   * 支払い未完了時のメッセージを取得
   */
  const getPendingPaymentMessage = () => {
    const paymentMethod = memberData?.paymentMethod || memberData?.payment_method;
    
    switch (true) {
      case isCreditCardPayment(paymentMethod):
        return 'クレジットカード情報の登録が必要です。下のボタンからクレジットカード情報を登録してください。';
      case isBankTransferLikePayment(paymentMethod):
        return (
        <>
          <span>お支払いが確認できていません。口座振替の手続きは通常数営業日かかる場合がございます。</span>
          <br />
          <span>手続き開始から1週間程度経過しても完了しない場合は、お客様サポートまでお問い合わせください。</span>
        </>
      );
      default:
        return 'お支払いをお願いいたします。';
    }
  };

  /**
   * 鍵システム設定待ち時のメッセージを取得（SETUP_PENDINGステータス用）
   */
  const getSetupPendingMessage = () => {
    return 'スマートロックの設定作業を行っております。設定完了までしばらくお待ちください。完了次第、メールでご連絡いたします。';
  };

  /**
   * 支払い設定ボタンを表示すべきかどうかを判定
   * クレジットカードと口座振替の場合のみtrue
   */
  const shouldShowPaymentSettingButton = () => {
    const paymentMethod = memberData?.paymentMethod || memberData?.payment_method;
    return isCreditCardPayment(paymentMethod) || isBankTransferLikePayment(paymentMethod);
  };

  /**
   * 支払い設定完了ボタンのテキストを取得
   */
  const getPaymentSettingButtonText = () => {
    const paymentMethod = memberData?.paymentMethod || memberData?.payment_method;
    
    if (isCreditCardPayment(paymentMethod)) {
      return 'クレジットカード情報を入力';
    }
    if (isBankTransferLikePayment(paymentMethod)) {
      return '振込情報を確認';
    }
    return '支払い設定を完了する';
  };

  /**
   * ステータス別の制限メッセージを取得
   */
  const getRestrictedAccessMessage = () => {
    switch (memberData?.status) {
      case 'PENDING':
      case 'PAYMENT_FAILED':
        return '決済完了後に利用可能になります';
      case 'SETUP_PENDING':
        return 'スマートロック設定完了後に利用可能になります';
      default:
        return '利用可能になるまでお待ちください';
    }
  };

  // 店舗履歴ページが表示されている場合
  if (showStoreHistory) {
    return <StoreHistoryPage memberData={memberData} onBack={handleBackFromStoreHistory} />;
  }

  // 初期ローディング中の表示
  if (loading) {
    return (
      <div style={homePageStyles.container}>
        <div style={homePageStyles.loadingMessage}>
          {getHomePageText('page.loadingAuth')}
        </div>
      </div>
    );
  }

  // 認証されていない場合（リダイレクト前の一時表示）
  if (!isAuthenticated()) {
    return (
      <div style={homePageStyles.container}>
        <div style={homePageStyles.loadingMessage}>
          {getHomePageText('page.redirectingLogin')}
        </div>
      </div>
    );
  }

  // 会員データローディング中
  if (memberLoading) {
    return (
      <div style={homePageStyles.container}>
        <div style={homePageStyles.header}>
          <h1 style={homePageStyles.title}>{getHomePageText('page.title')}</h1>
        </div>
        <div style={homePageStyles.loadingMessage}>
          {getHomePageText('page.loadingMember')}
        </div>
      </div>
    );
  }

  return (
    <div style={homePageStyles.container}>
      {/* ヘッダー部分 */}
      <div style={homePageStyles.header}>
        <h1 style={homePageStyles.title}>{getHomePageText('page.title')}</h1>
      </div>

      {/* 成功メッセージ表示 */}
      {message && (
        <div style={homePageStyles.successMessage}>
          {message}
        </div>
      )}

       {/* 支払い未完了時の警告表示（PENDINGステータス） */}
      {memberData && (memberData.status === 'PENDING' || memberData.status === 'PAYMENT_FAILED') && (
        <div style={{
          ...homePageStyles.errorMessage,
          backgroundColor: '#fff3cd',
          borderColor: '#ffeaa7',
          color: '#856404'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
            お支払いが完了していません
          </div>
          <div style={{ marginBottom: '15px' }}>
            {getPendingPaymentMessage()}
          </div>
          {/* クレジットカードと口座振替の場合のみボタンを表示 */}
          {shouldShowPaymentSettingButton() && (
            <button 
              style={{
                ...homePageStyles.button, 
                ...homePageStyles.primaryButton,
                backgroundColor: '#ffc107',
                borderColor: '#ffc107',
                color: '#212529'
              }}
              onClick={handleOpenPaymentSettings}
            >
              {getPaymentSettingButtonText()}
            </button>
          )}
        </div>
      )}

      {/* 鍵システム設定待ち時の案内表示（SETUP_PENDINGステータス） */}
      {memberData && memberData.status === 'SETUP_PENDING' && (
        <div style={{
          ...homePageStyles.errorMessage,
          backgroundColor: '#d1ecf1',
          borderColor: '#bee5eb',
          color: '#0c5460'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
            スマートロック設定中です
          </div>
          <div style={{ marginBottom: '15px' }}>
            {getSetupPendingMessage()}
          </div>
          <div style={{ fontSize: '14px', color: '#6c757d' }}>
            ※ 設定作業完了までの間は、各種機能をご利用いただけません。
          </div>
        </div>
      )}

      {/* 会員データエラー表示 */}
      {memberError && (
        <div style={homePageStyles.errorMessage}>
          <span>{`${getHomePageText('page.memberDataError')}: ${memberError}`}</span>
          <br />
          <button 
            style={{...homePageStyles.button, ...homePageStyles.refreshButton, marginTop: '10px'}}
            onClick={handleRefreshMemberData}
          >
            {getHomePageText('buttons.retry')}
          </button>
        </div>
      )}

      {/* 会員データなしの場合の表示 */}
      {!memberData && !memberError && (
        <div style={homePageStyles.errorMessage}>
          <span>{homePageFormatters.formatMessage(
            getHomePageText('page.memberDataNotFound'),
            { email: currentUser?.email || '' }
          )}</span>
          <br />
          <button 
            style={{...homePageStyles.button, ...homePageStyles.refreshButton, marginTop: '10px'}}
            onClick={handleRefreshMemberData}
          >
            {getHomePageText('buttons.research')}
          </button>
        </div>
      )}

      {/* 情報カードのグリッド表示 */}
      <div style={homePageStyles.contentGrid}>
        {/* アカウント情報カード */}
        <div style={homePageStyles.card}>
          <h3 style={homePageStyles.cardTitle}>{getHomePageText('cardTitles.accountInfo')}</h3>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.memberId')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">{memberData?.memberId || '取得中...'}</span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.email')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">{currentUser?.email || getHomePageText('status.notSet')}</span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.emailVerification')}:`}</span>
            <span 
              style={styleUtils.getVerificationBadgeStyle(currentUser?.emailVerified)}
            >
              {currentUser?.emailVerified ? getHomePageText('status.verified') : getHomePageText('status.unverified')}
            </span>
          </div>

           {/* 会員ステータス表示を追加 */}
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>利用状況:</span>
            <span 
              style={{
                ...homePageStyles.infoValue,
                color: getStatusColor(memberData?.status),
                fontWeight: 'bold'
              }}
            >
              {getStatusDisplayText(memberData?.status)}
            </span>
          </div>
        </div>

        {/* 会員基本情報カード */}
        <div style={homePageStyles.card}>
          <h3 style={homePageStyles.cardTitle}>{getHomePageText('cardTitles.memberBasicInfo')}</h3>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.fullName')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.fullName || memberData?.name || getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.fullNameKana')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.fullNameKana || memberData?.nameKana || getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.phoneNumber')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {homePageFormatters.maskPhoneNumber(memberData?.phoneNumber)}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.birthDate')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {homePageFormatters.formatDate(memberData?.birthDate || memberData?.birthday)}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.gender')}:`}</span>
            <span style={homePageStyles.infoValue}>{homePageFormatters.formatGender(memberData?.gender)}</span>
          </div>
        </div>

        {/* 住所情報カード */}
        <div style={homePageStyles.card}>
          <h3 style={homePageStyles.cardTitle}>{getHomePageText('cardTitles.addressInfo')}</h3>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.postalCode')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.postalCode ? `〒${memberData.postalCode}` : getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.prefecture')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.prefecture || getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.city')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.city || getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.streetAddress')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.address || getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.buildingRoom')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.buildingRoom || getHomePageText('status.notSet')}
            </span>
          </div>
        </div>

        {/* 店舗・契約情報カード */}
        <div style={homePageStyles.card}>
          <h3 style={homePageStyles.cardTitle}>{getHomePageText('cardTitles.storeContractInfo')}</h3>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.storeUsage')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {getStoreName(memberData?.shop || memberData?.storeName)}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.joinDate')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {homePageFormatters.formatDate(memberData?.serviceStartDate || memberData?.desiredDate)}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.paymentMethod')}:`}</span>
            <span style={homePageStyles.infoValue}>{homePageFormatters.formatPaymentMethod(memberData?.paymentMethod)}</span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.additionalStore')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">{memberData?.additionalStores || getHomePageText('status.noSelection')}</span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.shoeLocker')}:`}</span>
            <span style={homePageStyles.infoValue}>
              {homePageFormatters.formatShoesLockerStatus(
                memberData?.shoesLockerApplication
              )}
            </span>
          </div>
        </div>

        {/* 緊急連絡先カード */}
        <div style={homePageStyles.card}>
          <h3 style={homePageStyles.cardTitle}>{getHomePageText('cardTitles.emergencyContact')}</h3>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.emergencyContactName')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {memberData?.emergencyContactName || getHomePageText('status.notSet')}
            </span>
          </div>
          
          <div style={homePageStyles.infoItem}>
            <span style={homePageStyles.infoLabel}>{`${getHomePageText('labels.emergencyContactPhone')}:`}</span>
            <span style={homePageStyles.infoValue} translate="no">
              {homePageFormatters.maskPhoneNumber(memberData?.emergencyContactPhone)}
            </span>
          </div>
        </div>
      </div>

      {/* 利用不可の場合の案内メッセージ */}
      {!isFunctionAccessible() && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          color: '#856404',
          textAlign: 'center'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px' }}>
            現在、一部機能がご利用いただけません
          </div>
          <div style={{ fontSize: '14px' }}>
            {getRestrictedAccessMessage()}
          </div>
        </div>
      )}

      {/* アコーディオン式のアクションボタン */}
      <div style={{ marginTop: '40px' }}>
        {/* 1. アカウント設定セクション */}
        <div style={homePageStyles.accordionSection}>
          <button
            style={homePageStyles.accordionHeader}
            onMouseEnter={(e) => handleAccordionHeaderHover(e, true)}
            onMouseLeave={(e) => handleAccordionHeaderHover(e, false)}
            onClick={() => setAccountSectionOpen(!accountSectionOpen)}
          >
            <div style={homePageStyles.accordionHeaderContent}>
              <User size={20} style={homePageStyles.accordionIcon} />
              <span style={homePageStyles.accordionTitle}>アカウント設定</span>
            </div>
            {accountSectionOpen ? 
              <ChevronDown size={20} style={homePageStyles.accordionArrow} /> : 
              <ChevronRight size={20} style={homePageStyles.accordionArrow} />
            }
          </button>
          
          {accountSectionOpen && (
            <div style={homePageStyles.accordionContent}>
              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? handleOpenMemberEdit : undefined}
                disabled={!memberData || !isFunctionAccessible()}
              >
                <User size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>{getHomePageText('buttons.profileEdit')}</span>
              </button>

              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? handleOpenCreditCardEdit : undefined}
                disabled={!memberData || !isFunctionAccessible()}
              >
                <CreditCard size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>クレジットカード変更</span>
              </button>

              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? handleOpenEmailChange : undefined}
                disabled={!memberData || !isFunctionAccessible()}
              >
                <Mail size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>メールアドレス変更</span>
              </button>

              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? handleOpenPasswordChange : undefined}
                disabled={!memberData || !isFunctionAccessible()}
              >
                <Lock size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>パスワード変更</span>
              </button>
            </div>
          )}
        </div>

        {/* 2. 利用履歴セクション */}
        <div style={homePageStyles.accordionSection}>
          <button
            style={homePageStyles.accordionHeader}
            onMouseEnter={(e) => handleAccordionHeaderHover(e, true)}
            onMouseLeave={(e) => handleAccordionHeaderHover(e, false)}
            onClick={() => setUsageHistorySectionOpen(!usageHistorySectionOpen)}
          >
            <div style={homePageStyles.accordionHeaderContent}>
              <Store size={20} style={homePageStyles.accordionIcon} />
              <span style={homePageStyles.accordionTitle}>利用履歴</span>
            </div>
            {usageHistorySectionOpen ? 
              <ChevronDown size={20} style={homePageStyles.accordionArrow} /> : 
              <ChevronRight size={20} style={homePageStyles.accordionArrow} />
            }
          </button>
          
          {usageHistorySectionOpen && (
            <div style={homePageStyles.accordionContent}>
              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? handleOpenStoreHistory : undefined}
                disabled={!memberData || !isFunctionAccessible()}
              >
                <Store size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>{getHomePageText('buttons.storeHistory')}</span>
              </button>
            </div>
          )}
        </div>

        {/* 3. マニュアル・ガイドセクション */}
        <div style={homePageStyles.accordionSection}>
          <button
            style={homePageStyles.accordionHeader}
            onMouseEnter={(e) => handleAccordionHeaderHover(e, true)}
            onMouseLeave={(e) => handleAccordionHeaderHover(e, false)}
            onClick={() => setManualSectionOpen(!manualSectionOpen)}
          >
            <div style={homePageStyles.accordionHeaderContent}>
              <BookOpen size={20} style={homePageStyles.accordionIcon} />
              <span style={homePageStyles.accordionTitle}>マニュアル・ガイド</span>
            </div>
            {manualSectionOpen ? 
              <ChevronDown size={20} style={homePageStyles.accordionArrow} /> : 
              <ChevronRight size={20} style={homePageStyles.accordionArrow} />
            }
          </button>
          
          {manualSectionOpen && (
            <div style={homePageStyles.accordionContent}>
              {/* 店舗レイアウトボタン(条件付き表示) */}
              {storeUtils?.hasStoreLayout?.(getStoreName(memberData?.shop || memberData?.storeName)) && (
                <button
                  style={{
                    ...homePageStyles.accordionButton,
                    ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                  }}
                  onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                  onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                  onClick={isFunctionAccessible() ? () => handleOpenManual('store_layout') : undefined}
                  disabled={!isFunctionAccessible()}
                  title={!isFunctionAccessible() ? getRestrictedAccessMessage() : '店舗レイアウトマニュアル'}
                >
                  <BookOpen size={18} style={homePageStyles.accordionButtonIcon} />
                  <span style={homePageStyles.accordionButtonText}>{getHomePageText('buttons.storeLayout')}</span>
                </button>
              )}

                <button
                  style={{
                    ...homePageStyles.accordionButton,
                    ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                  }}
                  onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                  onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                  onClick={isFunctionAccessible() ? () => handleOpenManual('reservation') : undefined}
                  disabled={!isFunctionAccessible()}
                  title={!isFunctionAccessible() ? getRestrictedAccessMessage() : '予約マニュアル'}
                >
                  <BookOpen size={18} style={homePageStyles.accordionButtonIcon} />
                  <span style={homePageStyles.accordionButtonText}>{getHomePageText('buttons.reservationManual')}</span>
                </button>

              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? () => handleOpenManual('robot') : undefined}
                disabled={!isFunctionAccessible()}
                title={!isFunctionAccessible() ? getRestrictedAccessMessage() : 'ロボットマニュアル'}
              >
                <Bot size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>{getHomePageText('buttons.robotManual')}</span>
              </button>

              {/* スマートロック設定マニュアルボタン */}
              {memberData && storeUtils?.hasSmartLockManual?.(getStoreName(memberData?.shop || memberData?.storeName)) && (
                <button
                  style={{
                    ...homePageStyles.accordionButton,
                    ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                  }}
                  onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                  onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                  onClick={isFunctionAccessible() ? () => handleOpenManual('smart_lock') : undefined}
                  disabled={!isFunctionAccessible()}
                  title={!isFunctionAccessible() ? getRestrictedAccessMessage() : 'スマートロック設定マニュアル'}
                >
                  <Key size={18} style={homePageStyles.accordionButtonIcon} />
                  <span style={homePageStyles.accordionButtonText}>{getHomePageText('buttons.smartLockInstallManual')}</span>
                </button>
              )}

              <button
                style={{
                  ...homePageStyles.accordionButton,
                  ...(!isFunctionAccessible() ? homePageStyles.accordionButtonDisabled : {})
                }}
                onMouseEnter={(e) => handleAccordionButtonHover(e, true)}
                onMouseLeave={(e) => handleAccordionButtonHover(e, false)}
                onClick={isFunctionAccessible() ? openTermsInNewTab : undefined}
                disabled={!isFunctionAccessible()}
                title={!isFunctionAccessible() ? getRestrictedAccessMessage() : '会員規約'}
              >
                <FileText size={18} style={homePageStyles.accordionButtonIcon} />
                <span style={homePageStyles.accordionButtonText}>卓トレ会員規約</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ログアウトボタン */}
      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <button
          style={{...homePageStyles.button, ...homePageStyles.dangerButton}}
          onMouseEnter={(e) => {
            Object.assign(e.target.style, styleUtils.getButtonHoverStyle(buttonStyles.button, 'danger'));
          }}
          onMouseLeave={(e) => {
            Object.assign(e.target.style, styleUtils.getButtonLeaveStyle(buttonStyles.button, 'danger'));
          }}
          onClick={handleLogout}
        >
          {getHomePageText('buttons.logout')}
        </button>
      </div>

       {/* PDFビューアーモーダル */}
      <PdfViewer
        isOpen={showManualViewer}
        onClose={handleCloseManualViewer}
        manualType={selectedManualType}
        title={selectedManualTitle}
      />

      {/* 口座振替情報表示モーダル */}
      <BankTransferInfoModal
        isOpen={showBankTransferInfo}
        onClose={handleCloseBankTransferInfo}
        storeName={memberData?.storeName || memberData?.shop}
      />
    </div>
  );
};

export default HomePage;